import React, { useState, useEffect } from 'react';
import { Users, Target, ArrowRight, ArrowLeft, Check, Edit3 } from 'lucide-react';
import { MatchplayMatch, MatchplayTournament, MatchplayPlayer, UpdateHoleRequest } from '../../../types/matchplay-v2';
import { MatchplayScoring } from '../../../lib/matchplay-v2/scoring';
import { getParValues } from '../../../lib/matchplay-v2/utils';
import { HoleInput } from './HoleInput';

interface FourballScoringProps {
  match: MatchplayMatch;
  tournament: MatchplayTournament;
  team1Players: MatchplayPlayer[];
  team2Players: MatchplayPlayer[];
  currentHole: number;
  onHoleSubmit: (holeData: UpdateHoleRequest) => void;
  onHoleNavigation: (holeNumber: number) => void;
  isLoading: boolean;
}

export function FourballScoring({
  match,
  tournament,
  team1Players,
  team2Players,
  currentHole,
  onHoleSubmit,
  onHoleNavigation,
  isLoading
}: FourballScoringProps) {
  // All hooks must be called first, before any conditional logic
  const [editingHole, setEditingHole] = useState<number | null>(null);
  const [tempScores, setTempScores] = useState<{
    team1: [number | null, number | null];
    team2: [number | null, number | null];
  }>({ team1: [null, null], team2: [null, null] });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isHoleSelectionMode, setIsHoleSelectionMode] = useState(false);
  
  // Local state for current hole scores
  const [currentHoleScores, setCurrentHoleScores] = useState<{
    team1: [number | null, number | null];
    team2: [number | null, number | null];
  }>({ team1: [null, null], team2: [null, null] });

  // Default par values for 18 holes
  const defaultPars = getParValues(tournament.courseId);

  // Load current hole scores when currentHole changes
  useEffect(() => {
    const hole = match.holes.find(h => h.holeNumber === currentHole);
    if (hole) {
      setCurrentHoleScores({
        team1: [hole.team1GrossScores[0] || null, hole.team1GrossScores[1] || null],
        team2: [hole.team2GrossScores[0] || null, hole.team2GrossScores[1] || null]
      });
    } else {
      setCurrentHoleScores({ team1: [null, null], team2: [null, null] });
    }
  }, [currentHole, match.holes]);

  // Safety checks for player arrays - AFTER all hooks
  if (!team1Players || team1Players.length !== 2 || !team2Players || team2Players.length !== 2) {
    return (
      <div className="text-center py-8 text-red-500">
        <p>Error: Fourball requires exactly 2 players per team</p>
      </div>
    );
  }

  // Get scores for all holes
  const getHoleScores = (holeNumber: number) => {
    const hole = match.holes.find(h => h.holeNumber === holeNumber);
    if (!hole) return { team1: [null, null], team2: [null, null] };
    return {
      team1: [hole.team1GrossScores[0] || null, hole.team1GrossScores[1] || null],
      team2: [hole.team2GrossScores[0] || null, hole.team2GrossScores[1] || null]
    };
  };

  // Calculate team playing score (best ball)
  const getTeamPlayingScore = (holeNumber: number, teamIndex: number) => {
    const scores = getHoleScores(holeNumber);
    const teamScores = teamIndex === 0 ? scores.team1 : scores.team2;
    const players = teamIndex === 0 ? team1Players : team2Players;
    
    return MatchplayScoring.calculateTeamPlayingScore(
      teamScores.map(s => s || 0),
      players.map(p => p.handicap),
      holeNumber,
      'fourball'
    );
  };

  // Calculate totals
  const calculateTotal = (holes: number[], teamIndex: number) => {
    let total = 0;
    let count = 0;
    holes.forEach(holeNum => {
      const playingScore = getTeamPlayingScore(holeNum, teamIndex);
      if (playingScore) {
        total += playingScore;
        count++;
      }
    });
    return count > 0 ? total : null;
  };

  const frontNine = [1, 2, 3, 4, 5, 6, 7, 8, 9];
  const backNine = [10, 11, 12, 13, 14, 15, 16, 17, 18];
  
  const team1FrontTotal = calculateTotal(frontNine, 0);
  const team1BackTotal = calculateTotal(backNine, 0);
  const team1Total = team1FrontTotal && team1BackTotal ? team1FrontTotal + team1BackTotal : null;
  
  const team2FrontTotal = calculateTotal(frontNine, 1);
  const team2BackTotal = calculateTotal(backNine, 1);
  const team2Total = team2FrontTotal && team2BackTotal ? team2FrontTotal + team2BackTotal : null;

  const handleHoleClick = (holeNumber: number) => {
    if (holeNumber <= currentHole) {
      setEditingHole(holeNumber);
      const scores = getHoleScores(holeNumber);
      setTempScores(scores);
      setErrors({});
    }
  };

  const handleHoleSelection = (holeNumber: number) => {
    if (isHoleSelectionMode) {
      onHoleNavigation(holeNumber);
      setIsHoleSelectionMode(false);
    } else {
      handleHoleClick(holeNumber);
    }
  };

  const handleScoreUpdate = () => {
    if (editingHole === null) return;

    const holeData: UpdateHoleRequest = {
      holeNumber: editingHole,
      team1GrossScores: tempScores.team1.map(s => s || 0),
      team2GrossScores: tempScores.team2.map(s => s || 0)
    };

    onHoleSubmit(holeData);
    setEditingHole(null);
    setTempScores({ team1: [null, null], team2: [null, null] });
  };

  const handleCancelEdit = () => {
    setEditingHole(null);
    setTempScores({ team1: [null, null], team2: [null, null] });
    setErrors({});
  };

  // Submit current hole scores
  const handleSubmitCurrentHole = () => {
    // Use par values as defaults when null for any missing scores
    const team1Scores = currentHoleScores.team1.map(score => 
      score !== null ? score : defaultPars[currentHole - 1]
    );
    const team2Scores = currentHoleScores.team2.map(score => 
      score !== null ? score : defaultPars[currentHole - 1]
    );

    const holeData: UpdateHoleRequest = {
      holeNumber: currentHole,
      team1GrossScores: team1Scores,
      team2GrossScores: team2Scores
    };

    onHoleSubmit(holeData);
    setErrors({});
  };

  // Check if current hole is already completed
  const isCurrentHoleCompleted = () => {
    const hole = match.holes.find(h => h.holeNumber === currentHole);
    return hole !== undefined;
  };

  // Update current hole scores
  const updateCurrentHoleScore = (team: 'team1' | 'team2', playerIndex: number, score: number | null) => {
    setCurrentHoleScores(prev => ({
      ...prev,
      [team]: prev[team].map((s, i) => i === playerIndex ? score : s) as [number | null, number | null]
    }));
  };

  // Mobile-friendly hole cell component for team playing scores
  const TeamHoleCell = ({ holeNumber, teamIndex, isCurrentHole }: {
    holeNumber: number;
    teamIndex: number;
    isCurrentHole: boolean;
  }) => {
    const par = defaultPars[holeNumber - 1];
    const isClickable = isHoleSelectionMode || holeNumber <= currentHole;
    const playingScore = getTeamPlayingScore(holeNumber, teamIndex);

    return (
      <button
        onClick={() => isClickable && handleHoleSelection(holeNumber)}
        disabled={!isClickable}
        className={`h-8 w-8 text-xs font-bold rounded transition-all touch-manipulation ${
          isCurrentHole
            ? 'bg-primary-500 text-white shadow-md'
            : isHoleSelectionMode
            ? 'bg-yellow-100 text-yellow-700 border border-yellow-300 hover:bg-yellow-200'
            : playingScore === null
            ? 'bg-gray-100 text-gray-400 border border-gray-200'
            : playingScore === par
            ? teamIndex === 0 ? 'bg-blue-100 text-blue-700 border border-blue-300' : 'bg-red-100 text-red-700 border border-red-300'
            : playingScore < par
            ? 'bg-green-100 text-green-700 border border-green-300'
            : teamIndex === 0 ? 'bg-blue-200 text-blue-800 border border-blue-400' : 'bg-red-200 text-red-800 border border-red-400'
        } ${isClickable ? 'hover:scale-105 cursor-pointer' : 'cursor-not-allowed opacity-50'}`}
      >
        {playingScore || '-'}
      </button>
    );
  };

  return (
    <div className="w-full min-h-screen bg-gray-50">
      <div className="w-full max-w-none mx-auto p-2 space-y-4">
        
        {/* Hole Selection Button - Above Current Hole */}
        <div className="flex justify-center">
          <button
            onClick={() => setIsHoleSelectionMode(!isHoleSelectionMode)}
            className={`px-4 py-2 text-sm rounded-md transition-colors ${
              isHoleSelectionMode
                ? 'bg-yellow-500 text-white hover:bg-yellow-600'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            {isHoleSelectionMode ? 'Cancel Selection' : 'Select Hole'}
          </button>
        </div>
        
        {/* Current Hole Quick Entry - Moved to Top */}
        {currentHole <= 18 && (
          <div className="bg-primary-50 border border-primary-200 rounded-lg p-4 w-full">
            <h3 className="font-semibold text-primary-900 mb-4 text-center">
              Hole {currentHole} (Par {defaultPars[currentHole - 1]}) - Four Ball
            </h3>
            
            <div className="space-y-6">
              {/* Team A */}
              <div className="bg-blue-50 rounded-lg p-4">
                <h4 className="text-md font-semibold text-blue-900 mb-4 text-center">Team A</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <HoleInput
                    label={team1Players[0].playerName.split(' ')[0]}
                    value={currentHoleScores.team1[0]}
                    onChange={(score) => updateCurrentHoleScore('team1', 0, score)}
                    par={defaultPars[currentHole - 1]}
                    disabled={isLoading}
                  />
                  <HoleInput
                    label={team1Players[1].playerName.split(' ')[0]}
                    value={currentHoleScores.team1[1]}
                    onChange={(score) => updateCurrentHoleScore('team1', 1, score)}
                    par={defaultPars[currentHole - 1]}
                    disabled={isLoading}
                  />
                </div>
              </div>

              {/* Team B */}
              <div className="bg-red-50 rounded-lg p-4">
                <h4 className="text-md font-semibold text-red-900 mb-4 text-center">Team B</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <HoleInput
                    label={team2Players[0].playerName.split(' ')[0]}
                    value={currentHoleScores.team2[0]}
                    onChange={(score) => updateCurrentHoleScore('team2', 0, score)}
                    par={defaultPars[currentHole - 1]}
                    disabled={isLoading}
                  />
                  <HoleInput
                    label={team2Players[1].playerName.split(' ')[0]}
                    value={currentHoleScores.team2[1]}
                    onChange={(score) => updateCurrentHoleScore('team2', 1, score)}
                    par={defaultPars[currentHole - 1]}
                    disabled={isLoading}
                  />
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="mt-6 flex flex-col items-center gap-3">
              {errors.submit && (
                <p className="text-sm text-red-600 font-medium">{errors.submit}</p>
              )}
              
              <button
                onClick={handleSubmitCurrentHole}
                disabled={isLoading}
                className="w-full max-w-md px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed font-semibold text-lg touch-manipulation active:scale-95 transition-all shadow-md"
              >
                {isLoading ? (
                  <div className="flex items-center justify-center gap-3">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Submitting...
                  </div>
                ) : (
                  `${isCurrentHoleCompleted() ? 'Update' : 'Submit'} Hole ${currentHole} Scores`
                )}
              </button>
            </div>
          </div>
        )}

        {/* Team Headers - Compact */}
        <div className="grid grid-cols-2 gap-2">
          <div className="bg-blue-50 rounded-lg p-2 text-center">
            <h3 className="font-semibold text-blue-900 text-sm">Team A</h3>
            <div className="text-xs text-blue-700">
              {team1Players[0].playerName.split(' ')[0]} & {team1Players[1].playerName.split(' ')[0]}
            </div>
          </div>
          <div className="bg-red-50 rounded-lg p-2 text-center">
            <h3 className="font-semibold text-red-900 text-sm">Team B</h3>
            <div className="text-xs text-red-700">
              {team2Players[0].playerName.split(' ')[0]} & {team2Players[1].playerName.split(' ')[0]}
            </div>
          </div>
        </div>

        {/* Scorecard Layout - Full Width Mobile Optimized */}
        <div className="bg-white rounded-lg shadow-md p-2 w-full">
          <div className="space-y-4">
            
            {/* Front Nine */}
            <div className="w-full">
              <h3 className="text-sm font-semibold text-gray-900 mb-3 text-center">Front Nine</h3>
              
              {/* Hole Numbers */}
              <div className="grid grid-cols-11 gap-px mb-1 text-center">
                <div className="text-xs font-medium text-gray-600 p-1">Hole</div>
                {frontNine.map(hole => (
                  <div key={hole} className="text-xs font-medium text-gray-600 p-1">{hole}</div>
                ))}
                <div className="text-xs font-medium text-gray-600 p-1">IN</div>
              </div>

              {/* Par Row */}
              <div className="grid grid-cols-11 gap-px mb-2 text-center">
                <div className="text-xs text-gray-500 p-1">Par</div>
                {frontNine.map(hole => (
                  <div key={hole} className="text-xs text-gray-500 p-1">{defaultPars[hole - 1]}</div>
                ))}
                <div className="text-xs text-gray-500 p-1 font-semibold">{frontNine.reduce((sum, hole) => sum + defaultPars[hole - 1], 0)}</div>
              </div>

              {/* Team A Row */}
              <div className="grid grid-cols-11 gap-px mb-1 text-center">
                <div className="text-xs font-medium text-blue-700 p-1">Team A</div>
                {frontNine.map(hole => (
                  <div key={hole} className="flex justify-center p-1">
                    <TeamHoleCell 
                      holeNumber={hole} 
                      teamIndex={0} 
                      isCurrentHole={hole === currentHole}
                    />
                  </div>
                ))}
                <div className="text-xs font-bold bg-blue-50 rounded p-1">
                  {team1FrontTotal || '-'}
                </div>
              </div>

              {/* Team B Row */}
              <div className="grid grid-cols-11 gap-px text-center">
                <div className="text-xs font-medium text-red-700 p-1">Team B</div>
                {frontNine.map(hole => (
                  <div key={hole} className="flex justify-center p-1">
                    <TeamHoleCell 
                      holeNumber={hole} 
                      teamIndex={1} 
                      isCurrentHole={hole === currentHole}
                    />
                  </div>
                ))}
                <div className="text-xs font-bold bg-red-50 rounded p-1">
                  {team2FrontTotal || '-'}
                </div>
              </div>
            </div>

            {/* Back Nine */}
            <div className="w-full">
              <h3 className="text-sm font-semibold text-gray-900 mb-3 text-center">Back Nine</h3>
              
              {/* Hole Numbers */}
              <div className="grid grid-cols-12 gap-px mb-1 text-center">
                <div className="text-xs font-medium text-gray-600 p-1">Hole</div>
                {backNine.map(hole => (
                  <div key={hole} className="text-xs font-medium text-gray-600 p-1">{hole}</div>
                ))}
                <div className="text-xs font-medium text-gray-600 p-1">OUT</div>
                <div className="text-xs font-medium text-gray-600 p-1">Total</div>
              </div>

              {/* Par Row */}
              <div className="grid grid-cols-12 gap-px mb-2 text-center">
                <div className="text-xs text-gray-500 p-1">Par</div>
                {backNine.map(hole => (
                  <div key={hole} className="text-xs text-gray-500 p-1">{defaultPars[hole - 1]}</div>
                ))}
                <div className="text-xs text-gray-500 p-1 font-semibold">{backNine.reduce((sum, hole) => sum + defaultPars[hole - 1], 0)}</div>
                <div className="text-xs text-gray-500 p-1 font-semibold">72</div>
              </div>

              {/* Team A Row */}
              <div className="grid grid-cols-12 gap-px mb-1 text-center">
                <div className="text-xs font-medium text-blue-700 p-1">Team A</div>
                {backNine.map(hole => (
                  <div key={hole} className="flex justify-center p-1">
                    <TeamHoleCell 
                      holeNumber={hole} 
                      teamIndex={0} 
                      isCurrentHole={hole === currentHole}
                    />
                  </div>
                ))}
                <div className="text-xs font-bold bg-blue-50 rounded p-1">
                  {team1BackTotal || '-'}
                </div>
                <div className="text-xs font-bold bg-blue-100 rounded p-1">
                  {team1Total || '-'}
                </div>
              </div>

              {/* Team B Row */}
              <div className="grid grid-cols-12 gap-px text-center">
                <div className="text-xs font-medium text-red-700 p-1">Team B</div>
                {backNine.map(hole => (
                  <div key={hole} className="flex justify-center p-1">
                    <TeamHoleCell 
                      holeNumber={hole} 
                      teamIndex={1} 
                      isCurrentHole={hole === currentHole}
                    />
                  </div>
                ))}
                <div className="text-xs font-bold bg-red-50 rounded p-1">
                  {team2BackTotal || '-'}
                </div>
                <div className="text-xs font-bold bg-red-100 rounded p-1">
                  {team2Total || '-'}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Score Editing Modal */}
        {editingHole && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg p-6 max-w-lg w-full space-y-6 max-h-[90vh] overflow-y-auto">
              <div className="text-center">
                <h3 className="text-lg font-semibold text-gray-900">Edit Hole {editingHole}</h3>
                <p className="text-sm text-gray-600 mt-1">Par {defaultPars[editingHole - 1]} - Four Ball (Best Ball)</p>
              </div>

              <div className="space-y-6">
                {/* Team A Players */}
                <div className="bg-blue-50 rounded-lg p-4">
                  <h4 className="text-md font-semibold text-blue-900 mb-4 text-center">Team A</h4>
                  <div className="space-y-4">
                    <HoleInput
                      label={team1Players[0].playerName}
                      value={tempScores.team1[0]}
                      onChange={(score) => setTempScores(prev => ({
                        ...prev,
                        team1: [score, prev.team1[1]]
                      }))}
                      par={defaultPars[editingHole - 1]}
                      disabled={isLoading}
                    />
                    <HoleInput
                      label={team1Players[1].playerName}
                      value={tempScores.team1[1]}
                      onChange={(score) => setTempScores(prev => ({
                        ...prev,
                        team1: [prev.team1[0], score]
                      }))}
                      par={defaultPars[editingHole - 1]}
                      disabled={isLoading}
                    />
                  </div>
                </div>

                {/* Team B Players */}
                <div className="bg-red-50 rounded-lg p-4">
                  <h4 className="text-md font-semibold text-red-900 mb-4 text-center">Team B</h4>
                  <div className="space-y-4">
                    <HoleInput
                      label={team2Players[0].playerName}
                      value={tempScores.team2[0]}
                      onChange={(score) => setTempScores(prev => ({
                        ...prev,
                        team2: [score, prev.team2[1]]
                      }))}
                      par={defaultPars[editingHole - 1]}
                      disabled={isLoading}
                    />
                    <HoleInput
                      label={team2Players[1].playerName}
                      value={tempScores.team2[1]}
                      onChange={(score) => setTempScores(prev => ({
                        ...prev,
                        team2: [prev.team2[0], score]
                      }))}
                      par={defaultPars[editingHole - 1]}
                      disabled={isLoading}
                    />
                  </div>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={handleCancelEdit}
                  className="flex-1 px-4 py-2 text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleScoreUpdate}
                  disabled={isLoading}
                  className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors"
                >
                  {isLoading ? 'Saving...' : 'Update'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Quick Rules - Collapsible on Mobile */}
        <details className="bg-blue-50 border border-blue-200 rounded-lg overflow-hidden">
          <summary className="p-4 cursor-pointer font-semibold text-blue-900 hover:bg-blue-100 transition-colors touch-manipulation">
            Four Ball Rules (Tap to expand)
          </summary>
          <div className="px-4 pb-4 border-t border-blue-200">
            <ul className="text-sm text-blue-800 space-y-2 mt-3">
              <li>• Tap any completed hole to edit scores</li>
              <li>• Each player plays their own ball throughout</li>
              <li>• Team's playing score = best net score of the two players</li>
              <li>• Par values are pre-filled for quick entry</li>
              <li>• Green = Under par, Blue/Red = Par or over par by team</li>
            </ul>
          </div>
        </details>
      </div>
    </div>
  );
} 